// Home (doc 00 §5.1): know in five seconds what is running and what needs attention.
// Clients get a short variant: only what was shared with them.
import { Alert, Badge, Button, Card, Group, List, SimpleGrid, Skeleton, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import { PRODUCING_TEST_TYPES, TEST_TYPES, type Binding, type DashboardSummary } from '@twinlink/shared';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiCircle, FiGitBranch, FiGrid } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { PageBody, PageHeader } from '@web/app/shell/PageHeader';
import { EmptyState } from '@web/app/shell/StatePages';
import { RoleBadge } from '@web/components/StatusBadge';
import { useResolveBindings, useVizList } from '@web/features/dashboards/api';
import { useDataSources, useLastValues, useSignals } from '@web/features/data/api';
import { useAssetTree } from '@web/features/assets/api';
import { WellMap } from '@web/features/assets/WellMap';
import { useAuth } from '@web/lib/auth';
import { formatNumber, formatRelative } from '@web/lib/format';
import { useOrgContext, useOrgId, usePermissions } from '@web/lib/org';
import classes from './HomePage.module.css';
import { orgUnits } from '@web/lib/units';

const RATES = [
  { key: 'oil', role: 'oil_rate' },
  { key: 'gas', role: 'gas_rate' },
  { key: 'water', role: 'water_rate' },
] as const;
const rateBinding = (signalRole: string): Binding => ({ kind: 'role', equipmentTypeKey: 'separator_3ph', signalRole, scope: '$well' });
/** Signals per running operation read to tell whether its data is arriving. */
const HEALTH_SIGNALS = 8;
const STATUS_COLOR: Record<string, string> = { testing: 'green', standby: 'blue', rigup: 'orange', offline: 'gray', demob: 'gray' };

export function HomePage() {
  const { t } = useTranslation();
  const orgId = useOrgId();
  const org = useOrgContext(orgId);
  const me = useAuth((s) => s.me);
  const orgName = org.data?.name ?? t('nav.home');
  const who = <WhoAmI name={me?.user.name || me?.user.email || ''} role={org.data?.role ?? me?.user.platformRole ?? null} />;
  if (org.data?.role === 'client') return <ClientHome orgId={orgId} orgName={orgName} name={me?.user.name ?? ''} who={who} />;
  return <OperationsHome orgId={orgId} orgName={orgName} who={who} />;
}

/** Who is signed in and with which role — the first thing support asks. */
function WhoAmI({ name, role }: { name: string; role: string | null }) {
  const { t } = useTranslation();
  return (
    <Group gap="xs">
      <Text size="sm">{t('home.signedInAs', { name })}</Text>
      {role && <RoleBadge role={role} />}
    </Group>
  );
}

function OperationsHome({ orgId, orgName, who }: { orgId: string; orgName: string; who: ReactNode }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const tree = useAssetTree(orgId);
  const dashboards = useVizList(orgId, 'dashboards', permissions.has('dashboards.view'));

  const wells = useMemo(() => (tree.data?.fields ?? []).flatMap((f) => f.wells.map((w) => ({ ...w, fieldName: f.name }))), [tree.data]);
  const running = useMemo(() => wells.filter((w) => w.runningTest).map((w) => ({ id: w.id, name: w.name, site: w.fieldName, test: w.runningTest! })), [wells]);
  // Oil, gas and water add up over the operations that produce (well tests and plants) only.
  const producing = useMemo(() => running.filter((w) => PRODUCING_TEST_TYPES.includes(w.test.type)), [running]);
  const kinds = TEST_TYPES.map((k) => ({ kind: k, count: running.filter((w) => w.test.type === k).length })).filter((k) => k.count > 0);

  // Rates of every producing operation, plus a few signals of every running one to tell whether
  // data is arriving at all: one resolve call, one last-value call, refreshed quietly.
  const bindings = useMemo(() => producing.flatMap((w) => RATES.map((r) => ({ refId: `${w.id}.${r.key}`, binding: rateBinding(r.role), vars: { wellId: w.id } }))), [producing]);
  const resolved = useResolveBindings(orgId, bindings);
  const signals = useSignals(orgId, {}, running.length > 0);
  const watched = useMemo(() => {
    const byWell = new Map<string, string[]>();
    for (const w of running) byWell.set(w.id, []);
    for (const s of signals.data ?? []) {
      const list = s.links.wellId ? byWell.get(s.links.wellId) : undefined;
      if (list && list.length < HEALTH_SIGNALS && s.valueType === 'number') list.push(s.id);
    }
    return byWell;
  }, [running, signals.data]);
  const ids = useMemo(
    () => [...new Set([...Object.values(resolved.data ?? {}).filter((v): v is string => !!v), ...[...watched.values()].flat()])].sort().slice(0, 500),
    [resolved.data, watched],
  );
  const last = useLastValues(orgId, ids, 10_000);

  const value = (wellId: string, key: string) => {
    const id = resolved.data?.[`${wellId}.${key}`];
    const v = id ? last.data?.get(id) : undefined;
    return v && v.access === 'ok' && typeof v.value === 'number' ? v : null;
  };
  const totals = RATES.map((r) => {
    const values = producing.map((w) => value(w.id, r.key)).filter((v) => v !== null);
    const unitId = values[0]?.displayUnitId ?? null;
    // Every well reports in the user's unit for the quantity, so the values add up directly.
    return { key: r.key, total: values.length ? values.reduce((sum, v) => sum + (v.value as number), 0) : null, unit: orgUnits.unit(unitId ?? '')?.symbol ?? '', decimals: r.key === 'gas' ? 3 : 0 };
  });
  // Data health of each running operation, whatever its kind: silent when none of its signals
  // has a value, stale when values exist but none is fresh.
  const health = (wellId: string) => {
    const own = [...(watched.get(wellId) ?? []), ...RATES.map((r) => resolved.data?.[`${wellId}.${r.key}`])].filter((v): v is string => !!v);
    const values = own.map((id) => last.data?.get(id)).filter((v) => v && v.access === 'ok' && v.value !== null);
    if (values.length === 0) return 'silent';
    return values.some((v) => v!.quality !== 'stale') ? 'ok' : 'stale';
  };
  const checked = !!last.data && !!signals.data && (bindings.length === 0 || !!resolved.data);
  const stale = checked ? running.filter((w) => health(w.id) === 'stale') : [];
  const silent = checked ? running.filter((w) => health(w.id) === 'silent') : [];
  const recent = [...(dashboards.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 5);
  const loadingRates = producing.length > 0 && (resolved.isPending || (ids.length > 0 && last.isPending));

  return (
    <>
      <PageHeader title={orgName} crumbs={[{ label: t('nav.home') }]} />
      <PageBody>
        <Stack gap="md">
          {who}
          <SetupChecklist orgId={orgId} hasDashboards={(dashboards.data?.length ?? 0) > 0} />

          {(stale.length > 0 || silent.length > 0) && (
            <Alert color="orange" icon={<FiAlertTriangle />} title={t('home.attention')}>
              <List size="sm" spacing={2}>
                {stale.map((w) => (
                  <List.Item key={w.id}>{t('home.staleData', { well: `${w.site} / ${w.name}` })}</List.Item>
                ))}
                {silent.map((w) => (
                  <List.Item key={w.id}>{t('home.noData', { well: `${w.site} / ${w.name}` })}</List.Item>
                ))}
              </List>
            </Alert>
          )}

          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <Card padding="md">
              <Text className="tl-caption">{t('home.activeTests')}</Text>
              {tree.isPending ? <Skeleton height={34} mt={6} /> : <div className={`tl-num ${classes.kpi}`}>{running.length}</div>}
              <Text size="xs" c="dimmed">
                {kinds.length > 0 ? kinds.map((k) => t('home.byKind', { kind: t(`wellsites.testType.${k.kind}`), count: k.count })).join(' · ') : t('home.onWells', { count: wells.length })}
              </Text>
            </Card>
            {totals.map((k) => (
              <Card key={k.key} padding="md">
                <Text className="tl-caption">{t(`home.rates.${k.key}`)}</Text>
                {loadingRates ? (
                  <Skeleton height={34} mt={6} />
                ) : (
                  <div className={`tl-num ${classes.kpi}`}>
                    {k.total === null ? '—' : formatNumber(k.total, k.decimals)} <span className={classes.kpiUnit}>{k.total === null ? '' : k.unit}</span>
                  </div>
                )}
                <Text size="xs" c="dimmed">
                  {producing.length > 0 || running.length === 0 ? t('home.sumOfRunning') : t('home.noProducing')}
                </Text>
              </Card>
            ))}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Card padding={0}>
              <Text component="h2" fw={600} p="sm" c="var(--tl-text-strong)">
                {t('home.wells')}
              </Text>
              {tree.isPending ? (
                <Skeleton height={160} m="sm" />
              ) : wells.length === 0 ? (
                <EmptyState icon={<FiActivity size={22} />} title={t('wellsites.empty')} hint={t('wellsites.emptyHint')} />
              ) : (
                <Table.ScrollContainer minWidth={420}>
                  <Table verticalSpacing={7}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('wellsites.well')}</Table.Th>
                        <Table.Th>{t('home.status')}</Table.Th>
                        <Table.Th>{t('home.runningTests')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {wells.map((w) => (
                        <Table.Tr key={w.id}>
                          <Table.Td>
                            <Link to={`/o/${orgId}/assets/well/${w.id}`}>{w.name}</Link>
                            <Text size="xs" c="dimmed">
                              {w.fieldName}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" color={STATUS_COLOR[w.status] ?? 'gray'}>
                              {t(`wellsites.status.${w.status}`)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {w.runningTest ? (
                              <div>
                                <Link to={`/o/${orgId}/live?well=${w.id}`}>{w.runningTest.name}</Link>
                                <Text size="xs" c="dimmed">
                                  {t(`wellsites.testType.${w.runningTest.type}`)}
                                </Text>
                              </div>
                            ) : (
                              <Text size="sm" c="dimmed">
                                {t('wellsites.noRunningTest')}
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Card>
            <Card padding={0} style={{ overflow: 'hidden' }}>
              <WellMap wells={wells} hrefFor={(id) => `/o/${orgId}/assets/well/${id}`} height={320} />
            </Card>
          </SimpleGrid>

          {permissions.has('dashboards.view') && <Shortcuts orgId={orgId} title={t('home.recentDashboards')} items={recent} kind="dashboards" loading={dashboards.isPending} />}
        </Stack>
      </PageBody>
    </>
  );
}

function Shortcuts({ orgId, title, items, kind, loading }: { orgId: string; title: string; items: DashboardSummary[]; kind: 'dashboards' | 'schematics'; loading: boolean }) {
  const { t } = useTranslation();
  const Icon = kind === 'dashboards' ? FiGrid : FiGitBranch;
  return (
    <Card padding="sm">
      <Group justify="space-between" mb="xs">
        <Text component="h2" fw={600} c="var(--tl-text-strong)">
          {title}
        </Text>
        <Button component={Link} to={`/o/${orgId}/${kind}`} variant="subtle" size="compact-sm">
          {t('home.seeAll')}
        </Button>
      </Group>
      {loading ? (
        <Skeleton height={60} />
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t(`home.none.${kind}`)}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="sm">
          {items.map((d) => (
            <Link key={d.id} to={`/o/${orgId}/${kind}/${d.id}`} className={classes.shortcut}>
              <Icon size={16} aria-hidden />
              <span>
                <span className={classes.shortcutName}>{d.name}</span>
                <span className={classes.shortcutMeta}>{formatRelative(d.updatedAt)}</span>
              </span>
            </Link>
          ))}
        </SimpleGrid>
      )}
    </Card>
  );
}

/** First-run guide for Org Admins; disappears once every step is done. */
function SetupChecklist({ orgId, hasDashboards }: { orgId: string; hasDashboards: boolean }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const org = useOrgContext(orgId);
  const isAdmin = permissions.has('org.users.manage') && permissions.has('datasources.manage');
  const tree = useAssetTree(orgId, isAdmin);
  const sources = useDataSources(orgId, isAdmin);
  const signals = useSignals(orgId, {}, isAdmin);
  if (!isAdmin || tree.isPending || sources.isPending || signals.isPending) return null;
  const steps = [
    { key: 'assets', done: (tree.data?.fields ?? []).some((f) => f.wells.length > 0), to: 'assets' },
    { key: 'source', done: (sources.data?.length ?? 0) > 0, to: 'admin/datasources' },
    { key: 'signals', done: (signals.data?.length ?? 0) > 0, to: 'explorer/catalog' },
    { key: 'users', done: (org.data?.seats?.users ?? 0) > 1, to: 'admin/users' },
    { key: 'dashboard', done: hasDashboards, to: 'dashboards' },
  ];
  if (steps.every((s) => s.done)) return null;
  return (
    <Card padding="md">
      <Text component="h2" fw={600} c="var(--tl-text-strong)">
        {t('home.setup.title')}
      </Text>
      <Text size="sm" c="dimmed" mb="sm">
        {t('home.setup.progress', { done: steps.filter((s) => s.done).length, total: steps.length })}
      </Text>
      <Stack gap={6}>
        {steps.map((s) => (
          <Group key={s.key} gap="sm" wrap="nowrap">
            <ThemeIcon variant="light" color={s.done ? 'green' : 'gray'} size="sm" radius="xl" aria-hidden>
              {s.done ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
            </ThemeIcon>
            <Text size="sm" style={{ flex: 1 }} td={s.done ? 'line-through' : undefined} c={s.done ? 'dimmed' : undefined}>
              {t(`home.setup.${s.key}`)}
              <span className={classes.srOnly}> — {t(s.done ? 'home.setup.done' : 'home.setup.todo')}</span>
            </Text>
            {!s.done && (
              <Button component={Link} to={`/o/${orgId}/${s.to}`} size="compact-xs" variant="light">
                {t('home.setup.open')}
              </Button>
            )}
          </Group>
        ))}
      </Stack>
    </Card>
  );
}

function ClientHome({ orgId, orgName, name, who }: { orgId: string; orgName: string; name: string; who: ReactNode }) {
  const { t } = useTranslation();
  const dashboards = useVizList(orgId, 'dashboards');
  const schematics = useVizList(orgId, 'schematics');
  return (
    <>
      <PageHeader title={orgName} crumbs={[{ label: t('nav.home') }]} />
      <PageBody>
        <Stack gap="md">
          <div>
            <Text component="h2" fw={600} size="lg" c="var(--tl-text-strong)">
              {t('home.welcome', { name })}
            </Text>
            <Text size="sm">{t('home.clientIntro')}</Text>
          </div>
          {who}
          <Shortcuts orgId={orgId} title={t('nav.dashboards')} items={dashboards.data ?? []} kind="dashboards" loading={dashboards.isPending} />
          <Shortcuts orgId={orgId} title={t('nav.schematics')} items={schematics.data ?? []} kind="schematics" loading={schematics.isPending} />
        </Stack>
      </PageBody>
    </>
  );
}
